import { useState } from 'react';
import { useApp } from '@context/AppContext';
import { useFirebase } from '@/App';
import ColourPalette from '@components/ColourPalette';
import {
  Pencil, Paintbrush, Highlighter, PenTool, Eraser,
  Square, Circle, Minus, PaintBucket, Sticker, Spline,
  Type, MousePointer, Pipette, Blend, RotateCw, ScissorsLineDashed,
  User, Share2,
} from 'lucide-react';
import { STAMPS } from '@utils/stamps';

const SIZE_PRESETS = [
  { label: 'XS', value: 2 },
  { label: 'S', value: 6 },
  { label: 'M', value: 14 },
  { label: 'L', value: 30 },
  { label: 'XL', value: 60 },
];

const TABS = [
  { id: 'draw', label: 'Draw' },
  { id: 'shapes', label: 'Shapes' },
  { id: 'tools', label: 'Tools' },
  { id: 'more', label: 'More' },
];

const DRAW_TOOLS = [
  { id: 'pencil', icon: Pencil, label: 'Pencil' },
  { id: 'brush', icon: Paintbrush, label: 'Brush' },
  { id: 'marker', icon: Highlighter, label: 'Marker' },
  { id: 'pen', icon: PenTool, label: 'Pen' },
  { id: 'eraser', icon: Eraser, label: 'Eraser' },
];

const SHAPE_TOOLS = [
  { id: 'rect', icon: Square, label: 'Rectangle' },
  { id: 'ellipse', icon: Circle, label: 'Ellipse' },
  { id: 'line', icon: Minus, label: 'Line' },
  { id: 'bezier', icon: Spline, label: 'Bezier' },
  { id: 'fill', icon: PaintBucket, label: 'Fill' },
];

const ADVANCED_TOOLS = [
  { id: 'text', icon: Type, label: 'Text' },
  { id: 'select', icon: MousePointer, label: 'Select' },
  { id: 'eyedropper', icon: Pipette, label: 'Dropper' },
  { id: 'gradient', icon: Blend, label: 'Gradient' },
  { id: 'transform', icon: RotateCw, label: 'Transform' },
];

export default function Toolbar() {
  const { state, dispatch } = useApp();
  const firebase = useFirebase();
  const { canvas } = state;
  const [activeTab, setActiveTab] = useState('draw');
  const [stampPickerOpen, setStampPickerOpen] = useState(false);

  const handleShareSolo = async () => {
    for (const stroke of canvas.soloStrokes) {
      const { id, pathData, ...data } = stroke;
      await firebase.pushStroke(data);
    }
    dispatch({ type: 'SHARE_SOLO_STROKES' });
    dispatch({ type: 'ADD_TOAST', payload: { message: 'Solo strokes shared to room' } });
  };

  const renderToolButton = ({ id, icon: Icon, label }) => (
    <button
      key={id}
      onClick={() => dispatch({ type: 'SET_TOOL', payload: id })}
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all text-left ${
        canvas.tool === id
          ? 'bg-amber text-white shadow-sm'
          : 'text-slate-400 hover:text-white hover:bg-charcoal-light'
      }`}
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="text-[11px] font-display truncate">{label}</span>
    </button>
  );

  return (
    <div className="w-36 bg-charcoal flex flex-col shrink-0 h-full">
      {/* Tab bar — horizontal across the top */}
      <div className="flex border-b border-charcoal-light shrink-0">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex-1 py-2 text-[10px] font-mono font-medium transition-colors ${
              activeTab === id
                ? 'text-amber border-b-2 border-amber bg-charcoal-light/50'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2">
        {/* ===== DRAW TAB ===== */}
        {activeTab === 'draw' && (
          <>
            <div className="flex flex-col gap-0.5">
              {DRAW_TOOLS.map(renderToolButton)}
            </div>
            <div className="h-px bg-charcoal-light" />
            <ColourPalette />
            <div className="h-px bg-charcoal-light" />
            {/* Size */}
            <div>
              <span className="text-[10px] text-slate-400 font-mono uppercase block mb-1">Size</span>
              <div className="flex flex-wrap gap-1">
                {SIZE_PRESETS.map((p) => (
                  <button key={p.label} onClick={() => dispatch({ type: 'SET_SIZE', payload: p.value })}
                    className={`flex-1 min-w-[24px] h-6 text-[10px] font-mono rounded transition-colors ${
                      canvas.size === p.value ? 'bg-amber text-white' : 'bg-charcoal-light text-slate-400 hover:text-white'
                    }`}>
                    {p.label}
                  </button>
                ))}
              </div>
              <input type="range" min={1} max={80} value={canvas.size}
                onChange={(e) => dispatch({ type: 'SET_SIZE', payload: Number(e.target.value) })}
                className="w-full mt-1 h-1 accent-amber appearance-none bg-charcoal-light rounded cursor-pointer" />
            </div>
            <div className="h-px bg-charcoal-light" />
            {/* Opacity */}
            <div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-400 font-mono uppercase">Opacity</span>
                <span className="text-[10px] text-slate-400 font-mono">{Math.round(canvas.opacity * 100)}%</span>
              </div>
              <input type="range" min={10} max={100} value={Math.round(canvas.opacity * 100)}
                onChange={(e) => dispatch({ type: 'SET_OPACITY', payload: Number(e.target.value) / 100 })}
                className="w-full mt-1 h-1 accent-amber appearance-none bg-charcoal-light rounded cursor-pointer" />
            </div>
          </>
        )}

        {/* ===== SHAPES TAB ===== */}
        {activeTab === 'shapes' && (
          <>
            <div className="flex flex-col gap-0.5">
              {SHAPE_TOOLS.map(renderToolButton)}
            </div>
            <div className="h-px bg-charcoal-light" />
            {/* Stamp tool */}
            <div>
              <button
                onClick={() => dispatch({ type: 'SET_TOOL', payload: 'stamp' })}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all text-left ${
                  canvas.tool === 'stamp'
                    ? 'bg-amber text-white shadow-sm'
                    : 'text-slate-400 hover:text-white hover:bg-charcoal-light'
                }`}
              >
                <Sticker className="w-4 h-4 shrink-0" />
                <span className="text-[11px] font-display">Stamps</span>
                <span className="text-base ml-auto">{canvas.activeStamp?.emoji || '⭐'}</span>
              </button>
              {/* Stamp grid — always visible when stamp tool is active */}
              {canvas.tool === 'stamp' && (
                <div className="mt-1.5 bg-charcoal-light/50 rounded-lg p-1.5">
                  <p className="text-[10px] text-slate-400 font-display mb-1 px-0.5">Pick a sticker, then click on canvas</p>
                  <div className="grid grid-cols-4 gap-1">
                    {STAMPS.map(({ id, emoji, label }) => (
                      <button key={id} onClick={() => {
                        dispatch({ type: 'SET_STAMP', payload: { id, emoji } });
                      }}
                        title={label}
                        className={`h-8 flex items-center justify-center rounded text-lg transition-colors ${
                          canvas.activeStamp?.id === id
                            ? 'bg-amber/30 ring-1 ring-amber'
                            : 'hover:bg-charcoal-light'
                        }`}>
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="h-px bg-charcoal-light" />
            <ColourPalette />
            <div className="h-px bg-charcoal-light" />
            {/* Stroke width */}
            <div>
              <span className="text-[10px] text-slate-400 font-mono uppercase block mb-1">Stroke Width</span>
              <div className="flex flex-wrap gap-1">
                {SIZE_PRESETS.map((p) => (
                  <button key={p.label} onClick={() => dispatch({ type: 'SET_SIZE', payload: p.value })}
                    className={`flex-1 min-w-[24px] h-6 text-[10px] font-mono rounded transition-colors ${
                      canvas.size === p.value ? 'bg-amber text-white' : 'bg-charcoal-light text-slate-400 hover:text-white'
                    }`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ===== TOOLS (ADVANCED) TAB ===== */}
        {activeTab === 'tools' && (
          <>
            <div className="flex flex-col gap-0.5">
              {ADVANCED_TOOLS.map(renderToolButton)}
            </div>
            <div className="h-px bg-charcoal-light" />

            {/* Usage hints */}
            <div className="bg-charcoal-light/50 rounded-lg p-2">
              <p className="text-[10px] text-slate-300 font-display leading-relaxed">
                {canvas.tool === 'text' && '👆 Click on canvas to place text. Press Enter to commit.'}
                {canvas.tool === 'select' && '👆 Click a stroke to select it. Drag to move.'}
                {canvas.tool === 'eyedropper' && '👆 Click anywhere to sample that colour.'}
                {canvas.tool === 'gradient' && '👆 Click and drag to create a gradient rectangle.'}
                {canvas.tool === 'transform' && '👆 Click a stroke, then drag to rotate and scale.'}
                {!['text', 'select', 'eyedropper', 'gradient', 'transform'].includes(canvas.tool) && 'Select a tool above to see how to use it.'}
              </p>
            </div>

            <div className="h-px bg-charcoal-light" />
            <ColourPalette />

            {/* Secondary colour for gradient */}
            {canvas.tool === 'gradient' && (
              <>
                <div className="h-px bg-charcoal-light" />
                <div>
                  <span className="text-[10px] text-slate-400 font-mono uppercase block mb-1">2nd Colour</span>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-full border-2 border-slate-500 cursor-pointer hover:border-amber transition-colors"
                      style={{ backgroundColor: canvas.secondaryColour }}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'color';
                        input.value = canvas.secondaryColour;
                        input.onchange = (e) => dispatch({ type: 'SET_SECONDARY_COLOUR', payload: e.target.value });
                        input.click();
                      }}
                    />
                    <button onClick={() => {
                      const temp = canvas.colour;
                      dispatch({ type: 'SET_COLOUR', payload: canvas.secondaryColour });
                      dispatch({ type: 'SET_SECONDARY_COLOUR', payload: temp });
                    }} className="text-lg text-slate-400 hover:text-white" title="Swap colours">⇅</button>
                  </div>
                </div>
              </>
            )}

            <div className="h-px bg-charcoal-light" />
            {/* Size + opacity */}
            <div>
              <span className="text-[10px] text-slate-400 font-mono uppercase block mb-1">Size</span>
              <div className="flex flex-wrap gap-1">
                {SIZE_PRESETS.map((p) => (
                  <button key={p.label} onClick={() => dispatch({ type: 'SET_SIZE', payload: p.value })}
                    className={`flex-1 min-w-[24px] h-6 text-[10px] font-mono rounded transition-colors ${
                      canvas.size === p.value ? 'bg-amber text-white' : 'bg-charcoal-light text-slate-400 hover:text-white'
                    }`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ===== MORE TAB ===== */}
        {activeTab === 'more' && (
          <>
            {/* Solo mode */}
            <div>
              <span className="text-[10px] text-slate-400 font-mono uppercase block mb-1">Solo Mode</span>
              <button
                onClick={() => dispatch({ type: 'TOGGLE_SOLO_MODE' })}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg transition-all text-left ${
                  canvas.soloMode
                    ? 'bg-violet-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white hover:bg-charcoal-light'
                }`}
              >
                <User className="w-4 h-4 shrink-0" />
                <span className="text-[11px] font-display">{canvas.soloMode ? 'Exit Solo' : 'Go Solo'}</span>
              </button>
              {canvas.soloMode && canvas.soloStrokes.length > 0 && (
                <button onClick={handleShareSolo}
                  className="w-full mt-1 flex items-center gap-2 px-2 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors text-left">
                  <Share2 className="w-4 h-4 shrink-0" />
                  <span className="text-[11px] font-display">Share to Room ({canvas.soloStrokes.length})</span>
                </button>
              )}
            </div>

            <div className="h-px bg-charcoal-light" />

            {/* Layers */}
            <div>
              <span className="text-[10px] text-slate-400 font-mono uppercase block mb-1">Active Layer</span>
              <div className="flex gap-1">
                <button onClick={() => dispatch({ type: 'SET_ACTIVE_LAYER', payload: 'foreground' })}
                  className={`flex-1 py-1.5 text-[10px] font-mono rounded transition-colors ${
                    canvas.activeLayer === 'foreground' ? 'bg-amber text-white' : 'bg-charcoal-light text-slate-400 hover:text-white'
                  }`}>Foreground</button>
                <button onClick={() => dispatch({ type: 'SET_ACTIVE_LAYER', payload: 'background' })}
                  className={`flex-1 py-1.5 text-[10px] font-mono rounded transition-colors ${
                    canvas.activeLayer === 'background' ? 'bg-amber text-white' : 'bg-charcoal-light text-slate-400 hover:text-white'
                  }`}>Background</button>
              </div>
            </div>

            <div className="h-px bg-charcoal-light" />

            {/* Shortcuts */}
            <div>
              <span className="text-[10px] text-slate-400 font-mono uppercase block mb-1">Shortcuts</span>
              <div className="space-y-1 text-[10px] text-slate-500 font-display">
                <div className="flex justify-between"><span className="text-slate-300 font-mono">⌘Z</span><span>Undo</span></div>
                <div className="flex justify-between"><span className="text-slate-300 font-mono">Space</span><span>Pan canvas</span></div>
                <div className="flex justify-between"><span className="text-slate-300 font-mono">Scroll</span><span>Zoom</span></div>
                <div className="flex justify-between"><span className="text-slate-300 font-mono">DblClick</span><span>Finish bezier</span></div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
